import React from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, CalendarDays } from "lucide-react";
import api from "../lib/api";
import { useSEO } from "../components/site/Reveal";

export default function EventRegisterSuccess() {
  useSEO("Registration Confirmed", "Thank you for registering for the trip.");
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const instant = params.get("paid") || params.get("free");
  const [state, setState] = React.useState(instant ? "done" : "checking");
  const pollRef = React.useRef(null);
  const tries = React.useRef(0);

  React.useEffect(() => {
    if (instant) return;
    if (!sessionId) { setState("error"); return; }
    const check = async () => {
      tries.current += 1;
      try {
        const { data } = await api.get(`/events/registration-status/${sessionId}`);
        if (data.payment_status === "paid") { setState("done"); clearInterval(pollRef.current); }
        else if (tries.current > 8) { setState("pending"); clearInterval(pollRef.current); }
      } catch { if (tries.current > 8) { setState("error"); clearInterval(pollRef.current); } }
    };
    check();
    pollRef.current = setInterval(check, 2500);
    return () => clearInterval(pollRef.current);
  }, [sessionId, instant]);

  return (
    <div className="min-h-screen grid place-items-center px-6 py-28 bg-night">
      <div className="text-center max-w-lg" data-testid="event-register-success">
        {state === "checking" ? (
          <><Loader2 className="w-12 h-12 text-gold animate-spin mx-auto mb-6" /><h1 className="font-serif text-3xl text-cream">Confirming your registration…</h1></>
        ) : state === "done" ? (
          <>
            <CheckCircle2 className="w-16 h-16 text-emerald-400 mx-auto mb-6" />
            <h1 className="font-serif text-4xl text-cream mb-3">Ẹ ṣé! You're Registered</h1>
            <p className="text-cream/70 mb-8">Thank you for signing up. We've received your details and will be in touch shortly with the next steps, travel information and payment plan.</p>
            <Link to="/events" className="inline-flex items-center gap-2 bg-gold text-night font-semibold px-6 py-3 rounded-full"><CalendarDays className="w-4 h-4" /> Back to Events</Link>
          </>
        ) : state === "pending" ? (
          <><h1 className="font-serif text-3xl text-cream mb-3">Registration Received</h1><p className="text-cream/70 mb-8">Your payment is processing — you'll get a confirmation shortly.</p><Link to="/events" className="inline-flex bg-gold text-night font-semibold px-6 py-3 rounded-full">Back to Events</Link></>
        ) : (
          <><h1 className="font-serif text-3xl text-cream mb-3">Something went wrong</h1><p className="text-cream/70 mb-8">We couldn't confirm this registration. If you were charged, please contact us.</p><Link to="/events" className="inline-flex bg-gold text-night font-semibold px-6 py-3 rounded-full">Back to Events</Link></>
        )}
      </div>
    </div>
  );
}
